import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dimensions, LayoutChangeEvent, StyleSheet, View, ViewProps } from 'react-native'
import { differenceInCalendarDays } from 'date-fns'
import { InsightTimeType } from 'zens-store'
import { Button, ButtonProps, CalendarValue, Color, Radius, Spacing, Text, sanitizeCalendarValue } from 'zens-storybook'
import { Tooltip } from '../tooltip'

const { width: windowWidth } = Dimensions.get('window')
const viewportPadding = Spacing.M
const tooltipMaxWidth = windowWidth - viewportPadding * 2

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
  },
  button: {
    marginLeft: Spacing.XS,
  },
  selected: {
    backgroundColor: Color.selectedBlue,
    borderColor: Color.darkerBlue,
  },
  selectedText: {
    color: Color.darkerBlue,
  },
  disabledText: {
    color: 'rgb(218, 217, 222)',
  },
  buttonWrapper: {
    alignItems: 'center',
    position: 'relative',
  },
  disabledWrapper: {
    pointerEvents: 'none',
  },
  tooltipWrapper: {
    alignItems: 'center',
  },
  tooltipDay: {
    transform: [{ translateX: 90 }],
  },
  tooltipHour: {
    transform: [{ translateX: Spacing.XS }],
  },
  tooltipBubble: {
    paddingVertical: 4,
    paddingHorizontal: Spacing.M,
    borderRadius: Radius.S,
    backgroundColor: 'rgb(169, 158, 151)',
    maxWidth: tooltipMaxWidth,
  },
  tooltipText: {
    color: Color.simplyWhite,
    textAlign: 'center',
    fontSize: 14,
    flexShrink: 0,
    flexWrap: 'nowrap',
  },
})

interface Props extends ViewProps {
  value?: InsightTimeType
  onChange?: (value: InsightTimeType) => void
  period?: CalendarValue
}

const InsightsTimeSelect = (props: Props) => {
  const { value, onChange, style, period, ...otherProps } = props
  const { t } = useTranslation()

  const sanitizedPeriod = period ? sanitizeCalendarValue(period) : undefined
  const [startDate, endDate] = sanitizedPeriod || []
  const rangeInDays = startDate && endDate ? differenceInCalendarDays(endDate, startDate) + 1 : 0
  const hourlyDisabled = rangeInDays > 31
  const allDisabled = rangeInDays > 366

  const [hovered, setHovered] = useState<InsightTimeType | undefined>(undefined)

  useEffect(() => {
    if (hourlyDisabled && value !== 'day') {
      onChange?.('day')
    }
  }, [hourlyDisabled, onChange, value])

  useEffect(() => {
    if (!allDisabled && hovered === 'day') setHovered(undefined)
    if (!hourlyDisabled && hovered === 'hour') setHovered(undefined)
  }, [allDisabled, hourlyDisabled, hovered])

  const update = (value: InsightTimeType) => () => {
    onChange?.(value)
  }

  const renderButton = (
    key: InsightTimeType,
    config: ButtonProps & {
      selected: boolean
      disabled?: boolean
      tooltipMessage?: string
      tooltipBubbleHour?: any
    },
  ) => {
    const { selected, disabled, tooltipMessage, tooltipBubbleHour, ...otherProps } = config

    const button = (
      <Button
        round
        size="S"
        light
        style={styles.button}
        titleStyle={[selected && styles.selectedText, disabled && styles.disabledText]}
        contentContainerStyle={selected && styles.selected}
        disabled={disabled}
        {...otherProps}
      />
    )

    const tooltipPositionStyle = key === 'day' ? styles.tooltipDay : styles.tooltipHour

    const tooltipLabel = tooltipMessage || t('Too big date range selected')

    return (
      <Tooltip
        label={tooltipLabel}
        visible={disabled && hovered === key}
        style={styles.tooltipWrapper}
        containerStyle={[tooltipPositionStyle]}
        tooltipStyle={[styles.tooltipBubble, tooltipBubbleHour]}
        textStyle={styles.tooltipText}
        offset={Spacing.S}
      >
        <View
          style={[styles.buttonWrapper, disabled && styles.disabledWrapper]}
          onPointerEnter={() => disabled && setHovered(key)}
          onPointerLeave={() => hovered === key && setHovered(undefined)}
        >
          {button}
        </View>
      </Tooltip>
    )
  }

  return (
    <View style={[styles.container, style]} {...otherProps}>
      {renderButton('day', {
        title: t('Daily'),
        selected: value === 'day',
        onPress: update('day'),
        disabled: allDisabled,
        testID: 'InsightsTimeSelect.Day',
        tooltipMessage: t('The period may be a maximum of 366 days.'),
      })}
      {renderButton('hour', {
        title: t('Hourly'),
        selected: value === 'hour',
        onPress: update('hour'),
        disabled: hourlyDisabled,
        testID: 'InsightsTimeSelect.Hour',
        tooltipMessage: t('The period may be a maximum of 31 days.'),
        tooltipBubbleHour: { marginRight: 200 },
      })}
    </View>
  )
}

export default InsightsTimeSelect
